import React from "react";
import SEOComponent from "../../SEOComponent";
import OptimizedImage from "../../OptimizedImage";
import managedServicesHero from "../../../assets/MSP.jpg";

export default function ManagedServices() {
  return (
    <div className="bg-gradient-to-br from-[#482A7A] to-[#B31F7E]">
      <SEOComponent 
        title="Managed Service Offerings | Business Technology Consulting | AlifShams"
        description="Comprehensive managed service offerings for business technology. Expert solutions to streamline operations and drive business growth."
        keywords="managed services, business consulting, operational excellence, process optimization, business solutions, strategic consulting"
        url="https://digitalif.net/solutions/business-technology/managed-services"
        type="service"
      />
      
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 opacity-20">
          <OptimizedImage
            src={managedServicesHero}
            alt="Managed Services"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-8">
              <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
              Enterprise Managed Services
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
              Managed Service
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Excellence
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto mb-12 leading-relaxed">
              Enterprise-grade managed services designed to optimize operations, reduce costs, and accelerate digital transformation at scale
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#services" className="inline-flex items-center px-8 py-4 bg-white/20 backdrop-blur-lg hover:bg-white/30 text-white font-semibold rounded-lg transition-all duration-300 border border-white/20">
                Explore Solutions
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <a href="/contact" className="inline-flex items-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#482A7A] font-semibold rounded-lg transition-all duration-300">
                Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Overview */}
      <section className="pt-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">99.9% SLA</h3>
              <p className="text-white/80">Enterprise-grade service level agreements with guaranteed uptime and performance metrics</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">24/7 Support</h3>
              <p className="text-white/80">Round-the-clock monitoring and support from certified enterprise specialists</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Scalable</h3>
              <p className="text-white/80">Flexible solutions that scale with your business growth and changing requirements</p>
            </div>
          </div>
        </div>
      </section>

      {/* Managed Services Categories */}
      <section id="services" className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Managed Service Offerings
            </h2>
            <p className="text-xl text-white/90 max-w-4xl mx-auto">
              Enterprise-grade managed services designed to optimize operations, enhance security, and drive digital transformation across your organization
            </p>
          </div>
          
          <div className="space-y-16">
            {/* 1. Infrastructure & Network Managed Services */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-10 border border-white/20 hover:bg-white/20 transition-all duration-300">
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mr-6">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-2">Infrastructure & Network Services</h3>
                    <p className="text-white/80 text-lg">Enterprise-grade infrastructure management ensuring 99.9% uptime and optimal performance</p>
                  </div>
                </div>
                {/* <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white border border-white/30">Core Infrastructure</span> */}
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5  text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Network Operations Center (NOC)</h4>
                  </div>
                  <p className="text-slate-600 leading-relaxed">24/7 monitoring, incident management, SLA adherence, and proactive maintenance for enterprise IT and telecom networks with real-time alerting and automated remediation.</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Cloud Infrastructure Management</h4>
                  </div>
                  <p className="text-slate-600 leading-relaxed">Comprehensive AWS, Azure, and multi-cloud environment management including provisioning, cost optimization, security compliance, and performance tuning at enterprise scale.</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.242 0 1 1 0 01-1.415-1.415 5 5 0 017.072 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Enterprise Wireless Management</h4>
                  </div>
                  <p className="text-slate-600 leading-relaxed">HPE Aruba and Juniper enterprise wireless infrastructure with advanced performance monitoring, configuration management, and optimization for large-scale deployments.</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Data Center & Virtualization</h4>
                  </div>
                  <p className="text-slate-600 leading-relaxed">Enterprise data center operations including server health monitoring, automated backup & disaster recovery, capacity planning, and virtualization management with predictive analytics.</p>
                </div>
              </div>
            </div>

            {/* 2. AI, Automation & Digital Platforms */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center mb-6">
                <span className="text-3xl mr-4">🧠</span>
                <h3 className="text-2xl font-bold text-pink-600">AI, Automation & Digital Platforms Managed Services</h3>
              </div>
              <p className="text-gray-600 mb-6 text-lg">Turning digital transformation into continuous value delivery.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">AI Chatbot & Knowledge Assistant Management</h4>
                  <p className="text-sm text-gray-600">Managed GenAI or conversational AI platforms — training, fine-tuning, and monitoring performance.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Automation-as-a-Service</h4>
                  <p className="text-sm text-gray-600">Continuous RPA (UiPath, Blue Prism, etc.) monitoring, workflow optimization, and bot lifecycle management.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Data Analytics Platform Management</h4>
                  <p className="text-sm text-gray-600">Managed Power BI / Tableau environments, data pipeline health checks, and dashboard governance.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">AI Model Maintenance & Drift Monitoring</h4>
                  <p className="text-sm text-gray-600">For clients using predictive analytics or machine learning models — retraining and performance assurance.</p>
                </div>
              </div>
            </div>

            {/* 3. OSS / Telecom Managed Services */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center mb-6">
                <span className="text-3xl mr-4">🛰️</span>
                <h3 className="text-2xl font-bold text-pink-600">OSS / Telecom Managed Services</h3>
              </div>
              <p className="text-gray-600 mb-6 text-lg">Specialized offering to position ALIF strongly in telecom & service provider markets.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">OSS/BSS Platform Operations</h4>
                  <p className="text-sm text-gray-600">L1–L3 managed support for Ciena Blue Planet, ServiceNow Telecom, or custom OSS integrations.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Service Fulfilment & Assurance Managed Services</h4>
                  <p className="text-sm text-gray-600">Continuous configuration, orchestration monitoring, fault detection, and performance analytics.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Network Inventory & Resource Management</h4>
                  <p className="text-sm text-gray-600">Regular updates, integrity checks, and synchronization with live network data.</p>
                </div>
              </div>
            </div>

            {/* 4. Smart Infrastructure & PropTech */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center mb-6">
                <span className="text-3xl mr-4">🏙️</span>
                <h3 className="text-2xl font-bold text-pink-600">Smart Infrastructure & PropTech Managed Services</h3>
              </div>
              <p className="text-gray-600 mb-6 text-lg">Perfect for your domain in town planning and real estate tech.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Smart Building System Management</h4>
                  <p className="text-sm text-gray-600">Integrated monitoring for IoT sensors, energy systems, and automation dashboards.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Digital Twin Platform Operations</h4>
                  <p className="text-sm text-gray-600">Ongoing model updates, 3D data integration, and analytics validation.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">PropTech Platform Support</h4>
                  <p className="text-sm text-gray-600">End-to-end managed operations for property management, leasing, or asset optimization software.</p>
                </div>
              </div>
            </div>

            {/* 5. Cybersecurity & Compliance */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center mb-6">
                <span className="text-3xl mr-4">🔒</span>
                <h3 className="text-2xl font-bold text-pink-600">Cybersecurity & Compliance Managed Services</h3>
              </div>
              <p className="text-gray-600 mb-6 text-lg">A must-have layer in every digital managed service contract.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Managed SOC (Security Operations Center)</h4>
                  <p className="text-sm text-gray-600">Threat detection, log analysis, SIEM management, and incident response.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Vulnerability & Patch Management</h4>
                  <p className="text-sm text-gray-600">Continuous risk scanning, remediation, and compliance tracking.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Identity & Access Management</h4>
                  <p className="text-sm text-gray-600">Managed authentication, privilege management, and access reviews.</p>
                </div>
              </div>
            </div>

            {/* 6. Business & IT Operations */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center mb-6">
                <span className="text-3xl mr-4">🧩</span>
                <h3 className="text-2xl font-bold text-pink-600">Business & IT Operations Managed Services</h3>
              </div>
              <p className="text-gray-600 mb-6 text-lg">Ensuring organizations run efficiently through continuous governance and process support.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">ITSM Platform Management (ServiceNow, Jira, etc.)</h4>
                  <p className="text-sm text-gray-600">Workflow configuration, ticket lifecycle optimization, and SLA reporting.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Application & Integration Managed Services</h4>
                  <p className="text-sm text-gray-600">Monitoring, patching, and optimization of enterprise apps and APIs.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">PMO-as-a-Service</h4>
                  <p className="text-sm text-gray-600">Ongoing project governance, KPI tracking, and executive dashboards for client programs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 bg-gradient-to-br from-[#482A7A] to-[#B31F7E]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Why Partner With Us?
          </h2>
          <p className="text-xl text-white/90 mb-16 max-w-3xl mx-auto">
            We deliver exceptional managed services that drive real business results
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">
                Proven Excellence
              </h3>
              <p className="text-white/80">
                15+ years of delivering exceptional managed services with industry-leading expertise and innovation.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">
                Scalable Solutions
              </h3>
              <p className="text-white/80">
                Flexible managed services that grow with your business, from startups to enterprise organizations.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">
                Proactive Support
              </h3>
              <p className="text-white/80">
                24/7 monitoring and proactive maintenance to prevent issues before they impact your business operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#482A7A]">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how our managed services can drive your business forward
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-gradient-to-r from-[#B31F7E] to-[#D946EF] text-white px-8 py-4 rounded-full font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              Start Your Journey
            </a>
            <a href="tel:+1234567890" className="border-2 border-[#B31F7E] text-[#B31F7E] px-8 py-4 rounded-full font-semibold hover:bg-[#B31F7E] hover:text-white transition-all duration-300">
              Call Us Today
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}